//Library Imports
import { Popover, Tooltip, } from "antd";
import styled from "styled-components";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import store, { RootState } from "store";
import { useHistory, Link } from "react-router-dom";
//Local Imports
import Checkbox from "../../../modules/Common/Components/Checkbox";
import { ReactComponent as Ellipsis } from "../../../assets/img/icons/ArticlePopover/Elipsis.svg";
import { ReactComponent as CheckSquare } from "../../../assets/img/icons/CheckSquare.svg";
import { ReactComponent as CloneWorkflow } from "../../../assets/img/icons/CloneCopy.svg";
import { ReactComponent as TrashBin } from "../../../assets/img/icons/trash_bin.svg";
import analyticsGrey from "../../../assets/img/icons/analyticsGrey.svg";
import contacts from "../../../assets/img/icons/contacts.svg";
import WorkflowThumb from "../../../assets/img/WorkflowThumb.png";
import { cloneWorkflow, deleteWorkflow, resumeWorkflowAPI, updateWorkflowStatusAPI } from "../actions";
import { capitalizeFirstLetter } from "utils";
import { routes, inactivateModalText, cloneModalText, clonedWorkflowModalText, cloningWorkflowModalText, cloningWorkflowPermissionError } from "../constants";
import ConfirmDialog from "modules/Common/Components/ConfirmDialog";
import DialogBox from "modules/Common/Components/DialogBox";
import InfoPopover from "modules/Common/Components/InfoPopover";
import ConfirmDialogWithModal from "modules/Common/Components/ConfirmDialogWithModal";
import { AUTOMATION_WORKFLOW_CLONE, AUTOMATION_WORKFLOW_STATUS_UPDATE } from "constants/types/automationTypes";
import { hasAnyErrorNode } from "../utils";
import useQuery from "hooks/useQuery";
import { faBullseye } from "@fortawesome/free-solid-svg-icons";
import { WFStatusEnum } from "../interfaces";

interface IListingItem {
  workflowId: number;
  name: string;
  color: string;
  status: string;
  selectedWorkflowIds: number[];
  setSelectedWorkflowIds: (workflowIds: number[]) => void;
  isSearched: boolean;
  selectAllFlag: boolean;
  setSelectAllFlag: (value: boolean) => void;
  excludedWorkflowsIds: number[];
  setExcludedWorkflowsIds: (workflowIds: number[]) => void;
  shareProfileUuid: string | null;
  triggerId: number;
  triggerEmailSent: boolean;
  selectedEmailId: number;
}

const ListingItem: React.FC<IListingItem> = (props: IListingItem) => {
  // Props
  const {
    workflowId,
    name,
    color,
    status,
    selectedWorkflowIds,
    setSelectedWorkflowIds,
    isSearched,
    selectAllFlag,
    setSelectAllFlag,
    excludedWorkflowsIds,
    setExcludedWorkflowsIds,
    shareProfileUuid,
    triggerId,
    triggerEmailSent,
    selectedEmailId
  } = props;

  // Local Variables
  const history = useHistory();
  let query = useQuery();
  const popoverTextDeleteDisable = "Cannot Delete an Active Workflow";

  // Local States
  const [deleteConfirmation, setDeleteConfirmation] = useState<boolean>(false);
  const [cloneConfirmation, setCloneConfirmation] = useState<boolean>(false);
  const [isCloned, setIsCloned] = useState<boolean>(false);
  const [cloningWorkflow, setCloningWorkflow] = useState<boolean>(false);
  const [changeWorkflowStatusConfirmation, setChangeWorkflowStatusConfirmation] = useState<boolean>(false);

  // Redux states
  const userWorkflowsTotalCount: number = useSelector(
    (state: RootState) => state.automation.userWorkflows.totalCount
  );
  const userWorkflowsClonedWorkflowId: number = useSelector(
    (state: RootState) => state.automation.userWorkflows.clonedWorkflowId
  );
  const userWorkflowsError: boolean = useSelector(
    (state: RootState) => state.automation.userWorkflows.error
  );
  const userWorkflowsMessage: string = useSelector(
    (state: RootState) => state.automation.userWorkflows.message
  );
  const workflow = useSelector((state: RootState) => state.automation.workflow?.flow);
  const errorNodes = useSelector((state: RootState) => state.automation.workflow.detail.errorNodes);

  const errorInAnyNode = hasAnyErrorNode(workflow) || errorNodes.length > 0;
  const fromQuery = query.get("from") || "default";

  useEffect(() => {
    if (!cloneConfirmation) {
      setIsCloned(false);
      store.dispatch({ type: AUTOMATION_WORKFLOW_CLONE.RESET });
    }
  }, [cloneConfirmation]);

  // Component Logic
  const confirmDeleteWorkflowHandler = () => {
    deleteWorkflow([workflowId], isSearched, false, [], "", "");
    setDeleteConfirmation(false);
  };

  const cancelConfirmationHandler = () => {
    setDeleteConfirmation(false);
  };

  const confirmCloneWorkflowHandler = () => {
    if (userWorkflowsError) {
      // Permission error (onClick OK)
      setCloneConfirmation(false);
      store.dispatch({ type: AUTOMATION_WORKFLOW_CLONE.RESET });
    } else {
      if (isCloned) {
        // When Cloned (onClick Edit Workflow)
        history.push(`/automation/details?workflow_id=${userWorkflowsClonedWorkflowId}`);
        setIsCloned(false);
      } else {
        // Before Cloning (onClick Proceed)
        setCloningWorkflow(true);
        cloneWorkflow(workflowId)
          .then((res: any) => {
            setCloningWorkflow(false);
            setIsCloned(true);
          });
      }
    }
  };

  const cancelCloneConfirmationHandler = () => {
    setCloneConfirmation(false)
    isCloned ?? setIsCloned(false);
  };

  const activateInActivateWorkflow = () => {
    setChangeWorkflowStatusConfirmation(true);
  };

  const inactivateActiveWorkflow = () => {
    store.dispatch({
      type: AUTOMATION_WORKFLOW_STATUS_UPDATE.LOAD,
    });
    resumeWorkflowAPI({ workflowId, status: "active" })
      .then((res: any) => {
        if (res.data.success) {
          store.dispatch({
            type: AUTOMATION_WORKFLOW_STATUS_UPDATE.LOAD_SUCCESS,
            payload: { workflowId, workflowStatus: "active" },
          });
        } else {
          store.dispatch({
            type: AUTOMATION_WORKFLOW_STATUS_UPDATE.LOAD_FAILED,
          });
        }
      })
  };

  const content = () => (
    <div className="workflow-dropdown-inner">
      <div className="content-popover option">
        <div className={`item-name d-flex justify-content-between ${status === "draft" ? "disabled-option" : ""}`} >
          <span onClick={status === "active" ? activateInActivateWorkflow : inactivateActiveWorkflow} className={`pointer ${(status === "inactive" && errorInAnyNode)}`}>
            <CheckSquare />
            <span className="option-text">{status === "active" ? "Make Inactive" : "Make Active"}</span>
          </span>
        </div>
      </div>
      <div className="content-popover option">
        <div className={`item-name d-flex justify-content-between ${triggerId === 10 ? "disabled-option" : ""}`}>
          <span onClick={() => setCloneConfirmation(true)} className="pointer">
            <StyledCloneWorkflow />
            <span className="option-text">Clone</span>
          </span>
        </div>
      </div>
      <div className="content-popover">
        <div className={`item-name d-flex justify-content-between ${status === "active" ? "disabled-option" : ""}`}>
          <span onClick={() => setDeleteConfirmation(true)} className="pointer">
            <TrashBin />
            <span className="option-text">Delete Workflow</span>
          </span>
          <span hidden={status !== "active"} className="delete-icon pointer">
            <InfoPopover infoText={popoverTextDeleteDisable} width={225} />
          </span>
        </div>
      </div>
    </div>
  );

  const workflowRedirectionClickHandler = () => {
    history.push(`${routes.DETAIL}?workflow_id=${workflowId}&redirect=automation`);
  };

  const checkboxClickHandler = (checked: boolean) => {
    if (checked) {
      setSelectedWorkflowIds([...selectedWorkflowIds, workflowId]);
      if ([...selectedWorkflowIds, workflowId].length === userWorkflowsTotalCount) setSelectAllFlag(true);
      if (selectAllFlag) {
        const index = excludedWorkflowsIds.indexOf(workflowId);
        if (index > -1) {
          excludedWorkflowsIds.splice(index, 1);
        }
      }
    } else {
      setSelectedWorkflowIds(selectedWorkflowIds.filter((id) => id.toString() !== workflowId.toString()));
      if (selectAllFlag) setExcludedWorkflowsIds([...excludedWorkflowsIds, workflowId]);
    }
  };

  const proceedChangeWorkflowStatusConfirmationHandler = () => {
    updateWorkflowStatusAPI(workflowId, "inactive");
    setChangeWorkflowStatusConfirmation(false);
  };

  const closeChangeWorkflowStatusConfirmationHandler = () => {
    setChangeWorkflowStatusConfirmation(false);
  };

  return (
    <>
      <Wrapper id={workflowId.toString()}>
        <td>
          <Checkbox
            disabled={status === "active" ? true : false}
            checked={selectedWorkflowIds.includes(workflowId)}
            onChange={(e: any) => checkboxClickHandler(e.target.checked)}
          />
        </td>
        <td>
          <div className="thumb" onClick={workflowRedirectionClickHandler}>
            <span style={{ background: `${color}` }}></span>
            <div className="image-resize" style={{ backgroundImage: `url(${WorkflowThumb})` }}></div>
            <img src={WorkflowThumb} alt="thumb" />
          </div>
        </td>
        <td>
          <h3 className="cursor-ponter" onClick={workflowRedirectionClickHandler}>
            {name}
          </h3>
          <div className="status-workflow" style={{ background: `${color}` }}>
            {capitalizeFirstLetter(status)}
          </div>
        </td>
        <td className={`d-flex ${triggerEmailSent ? "justify-content-between" : "justify-content-end"}`}>
          {status !== WFStatusEnum.DRAFT &&
            <>
              <div
                hidden={!triggerEmailSent}
              >
                <Tooltip placement="top" title="Analytics">
                  <Link to={`/analytics?workflow_id=${workflowId}&redirect=automation`}>
                    <img src={analyticsGrey} alt="icon" />
                  </Link>
                </Tooltip>
              </div>
              <div
                hidden={!triggerEmailSent}
              >
                <Tooltip placement="top" title="Contacts">
                  <Link
                    to={
                      triggerId === 10
                        ? `/audiences/people?uuid=${shareProfileUuid}&workflow_id=${workflowId}${fromQuery === "campaigns" ? "&from=campaigns" : ''
                        }&redirect=automation`
                        : `/audiences/people?email_id=${selectedEmailId}&workflow_id=${workflowId}${fromQuery === "campaigns" ? "&from=campaigns" : ''
                        }&redirect=automation`
                    }
                  >
                    <img src={contacts} alt="icon" />
                  </Link>
                </Tooltip>
              </div>
            </>}
          {!deleteConfirmation ? (
            <Popover
              placement="leftBottom"
              content={content}
              trigger="click"
              getPopupContainer={(e) => e as HTMLElement}
              overlayClassName="workflow-dropdown"
            >
              <div className="menu-ellips">
                <Ellipsis />
              </div>
            </Popover>
          ) : null}
        </td>
      </Wrapper>
      {deleteConfirmation ? (
        <DialogWrapper>
          <BackOverlay />
          <ConfirmDialogWithModal
            cancel={"Cancel"}
            confirm={"Yes"}
            onConfirm={confirmDeleteWorkflowHandler}
            onCancel={cancelConfirmationHandler}
            // icon={whiteRoy}
            title={"The selected workflow will be deleted"}
          />
        </DialogWrapper>
      ) : null}
      {cloneConfirmation ?
        <DialogBox
          heading=""
          isBodyVisible={false}
          isWarning={true}
          bodyText={userWorkflowsMessage?.length !== 0 ? userWorkflowsMessage : userWorkflowsError ? cloningWorkflowPermissionError : cloningWorkflow ? cloningWorkflowModalText : isCloned ? clonedWorkflowModalText : cloneModalText}
          isModalOpen={cloneConfirmation}
          closeHandler={cancelCloneConfirmationHandler}
          closeBtnText="Cancel"
          isCloseBtnVisible={true}
          proceedBtnText={`${userWorkflowsError ? "OK" : isCloned ? "Edit Workflow" : "Proceed"}`}
          proceedHandler={confirmCloneWorkflowHandler}
          isClone={true}
          clickOutsideClose={false}
          isWorkflowCloned={isCloned}
          cloningWorkflow={cloningWorkflow}
        /> : null}
      <DialogBox
        heading=""
        isBodyVisible={false}
        isWarning={true}
        bodyText={inactivateModalText}
        isModalOpen={changeWorkflowStatusConfirmation}
        closeHandler={closeChangeWorkflowStatusConfirmationHandler}
        closeBtnText="Cancel"
        isCloseBtnVisible={true}
        proceedBtnText="Proceed"
        proceedHandler={proceedChangeWorkflowStatusConfirmationHandler}
        clickOutsideClose={false}
        isMakeActive={true}
      />
    </>
  );
};

export default ListingItem;

const BackOverlay = styled.div`
  position: fixed;
  background: grey;
  opacity: 0.2;
  height: 100vh;
  width: 100vw;
  top: 0;
  left: 0;
`;

const DialogWrapper = styled.div`
  z-index: 17;
  top: 50%;
  left: 50%;
  position: fixed;
`;

const StyledCloneWorkflow = styled(CloneWorkflow)`
  width: 16px;
  height: 16px;
`;

const Wrapper = styled.tr`
  .div-style {
    display: flex;
    justify-content: space-between;
  }
  .cursor-ponter {
    cursor: pointer;
  }
  .workflow-dropdown {
    .workflow-dropdown-inner {
      padding: 16px 24px;
      .header-popover .item-name{
        color: #7E7E7E;
      }
      .option{
        margin-bottom: 16px;
      }
      .content-popover {
        background-color: unset;
        padding: 0;
        .option-text{
          font-family: 'Roboto';
          font-size: 16px;
          font-weight: 700;
          line-height: 24px;
          letter-spacing: -0.3999999761581421px;
        }
        .option-text:hover{
          color: #27ce0a;
        }
        .delete-text {
          display: inline-block;
          margin-top: 2px;
        }
        span:nth-child(1) {
          display: inline-flex;
          align-items: center;
        }
        .item-name {
          padding: 0;
          cursor: pointer;
          &.disabled-option {
            cursor: not-allowed;
            opacity: 0.5;
            .pointer {
              pointer-events: none;
            }
          }
          svg{
            margin-right: 16px;
          }
        }
      }
    }
  }
  .menu-ellips {
    cursor: pointer;
    padding: 0 5px;
  }
  h3 {
    color: #000000;
    font-weight: 500;
    font-size: 20px;
    font-family: "Roboto Slab", serif;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: 100%;
    margin-bottom: 6px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 14px;
    }
  }
  .status-workflow {
    color: #fff;
    font-size: 13px;
    font-weight: 500;
    font-family: "Roboto", sans-serif;
    padding: 2px 10px;
    border-radius: 100px;
    display: inline;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 11px;
    }
  }
  .thumb {
    position: relative;
    cursor: pointer;
    span {
      width: 8px;
      border-radius: 6px 0 0 6px;
      height: 82px;
      position: absolute;
      left: 0;
      top: 0;
    }
    .image-resize {
      background-size: cover;
      background-repeat: no-repeat;
      z-index: 1;
      width: 102px;
      min-width: 102px;
      height: 82px;
      border-radius: 0 6px 6px 0;
      position: absolute;
      right: 0;
      top: 0;
    }
    img {
      border-radius: 6px;
      width: 102px;
      min-width: 102px;
      height: 82px;
      opacity: 0;
    }
  }
  .workflow-dropdown-inner {
    .item-name {
      .delete-icon {
        img {
          height: 16px;
          /* margin-bottom: 5px; */
        }
      }
    }
  }
  .ant-popover{
    z-index: 9;
  }
  .ant-popover-placement-leftBottom > .ant-popover-content > .ant-popover-arrow {
    bottom: 2px;
    border-radius: 3px;
  }
  .workflow-dropdown .ant-popover-inner {
    margin-bottom: -25px;
  }
  .ant-popover-placement-leftBottom > .ant-popover-content > .ant-popover-arrow{
    box-shadow: 6px -5px 7px rgba(0, 0, 0, 0.07);
  }
`;
